import React, { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { getDefaultSorting, InfiniteScrollTable, Panel } from '@ydistri/ds';
import { CalculationStatus, SkuRedistributionResponse } from '@ydistri/api-sdk';
import {
  CalculationCategoryPairingRequest,
  useGetCalculationCategoryPairingsQuery,
} from '../../../../../../Calculations/apiCalculationsCategories';
import { addToast, createDebugLog } from '@ydistri/utils';
import { useAppDispatch } from '../../../../../../../store';
import { useSelectedPickingPositionFromUrl } from '../../useSelectedPickingPositionFromUrl';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import {
  selectSKURedistribution,
  setRedistributionPairingCount,
} from '../../../../../../Calculations/calculationsSlice';
import { signalrClient } from '../../../../../../../signalr/client';
import { SignalCalculationStatusChanged } from '../../../../../../Calculations/calculationsTypes';
import { useSkuRedistributionTableColumns } from './useSkuRedistributionTableColumns';
import { computeRemSize } from '@ydistri/theme';
import { buildRedistributionConditions } from './skuRedistributionConditions';

const debugLog = createDebugLog('CalculationRedistributionBody');

const TABLE_ID = 'SkuRedistribution';
const TABLE_OFFSET = 170; //just a default value, will be recomputed right away - to compensate for the table's header as it is rendered outside the table's container

interface SkuRedistributionTableProps {
  calculationId: number;
  categoryId: number;
}

const StyledSkuRedistributionTable = React.memo(styled(
  InfiniteScrollTable<SkuRedistributionResponse, CalculationCategoryPairingRequest>,
)`
  .ant-table-row {
    cursor: pointer;
  }

  .ant-table-small .ant-table-tbody > tr:not(:first-child) > td {
    padding-top: ${computeRemSize(2)} !important;
    padding-bottom: ${computeRemSize(2)} !important;
  }

  .selected-row > td.ant-table-cell-row-hover,
  .selected-row > td.ant-table-cell {
    background-color: #f29718 !important;
    color: white;
  }
`);

const computeTableSize = (newSize: number) => Math.max(newSize, 190);

const getRowKey = (row: SkuRedistributionResponse) =>
  `${row.sourceSku.skuId}#${row.targetSku.skuId}`;

const CALCULATION_REDISTRIBUTION_PAGE_SIZE = 300;

const SkuRedistributionTable: React.FC<SkuRedistributionTableProps> = ({
  calculationId,
  categoryId,
}) => {
  const dispatch = useAppDispatch();

  const selectedItem = useSelectedPickingPositionFromUrl();

  const columns = useSkuRedistributionTableColumns();

  const redistributionFilterBrandId = useSelector(
    state => state.calculations.redistributionFilterBrandId,
  );

  const redistributionFilterSourceSkuClassIds = useSelector(
    state => state.calculations.redistributionFilterSourceSkuClassIds,
  );

  const redistributionFilterTargetSkuClassIds = useSelector(
    state => state.calculations.redistributionFilterTargetSkuClassIds,
  );
  const redistributionFilterOutOfStock = useSelector(
    state => state.calculations.redistributionFilterOutOfStock,
  );
  const redistributionFilterForced = useSelector(
    state => state.calculations.redistributionFilterForced,
  );

  const [params, setParams] = useState<CalculationCategoryPairingRequest>({
    calculationId,
    categoryId,
    skip: 0,
    top: CALCULATION_REDISTRIBUTION_PAGE_SIZE,
    sortings: getDefaultSorting(columns),
  });

  const ref = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState<number>(400);
  const [tableHeightOffset, setTableHeightOffset] = useState<number>(TABLE_OFFSET);
  const [resizeObserver, setResizeObserver] = useState<ResizeObserver | null>(null);
  const [, setSearchParams] = useSearchParams();

  const searchQuery = useSelector(state => state.calculations.redistributionSearchQuery);

  const resizeCallback = useCallback(() => {
    const newHeight = Math.floor(ref.current?.clientHeight ?? 400);
    debugLog('new height', newHeight);
    setTableHeight(newHeight);
  }, []);

  const changeTableHeightOffset = useCallback(() => {
    const tableHeader = document.getElementById(TABLE_ID)?.querySelector('.ant-table-header');
    if (tableHeader) {
      setTableHeightOffset(tableHeader.clientHeight + 40);
    }
  }, []);

  // inform the user that the calculation is running (because the table content may change once
  // the calculation is completed)
  useEffect(() => {
    const listener = (signal: SignalCalculationStatusChanged) => {
      if (signal.status === CalculationStatus.Running) {
        dispatch(addToast({ message: 'Calculation is running' }));
      } else if (signal.status === CalculationStatus.Completed) {
        dispatch(addToast({ message: 'Calculation completed' }));
      }
    };

    signalrClient.on('CalculationStatusChanged', listener);

    return () => {
      signalrClient.off('CalculationStatusChanged', listener);
    };
  }, [dispatch]);

  useEffect(() => {
    if (ref.current) {
      if (resizeObserver === null) {
        debugLog('setting up resize observer');
        const initialHeight = Math.floor(ref.current.clientHeight);
        debugLog('initial height', initialHeight);
        changeTableHeightOffset();

        setTableHeight(initialHeight);

        window.addEventListener('resize', resizeCallback);

        //when the grandparent element is resized (the section), we need to recompute the table's height
        const tmpObserver = new ResizeObserver(entries => {
          for (const entry of entries) {
            const elementSize = entry.contentBoxSize[0].blockSize;
            const newSize = Math.floor(elementSize);

            if (newSize !== tableHeight) {
              const newComputedHeight = computeTableSize(newSize);
              setTableHeight(newComputedHeight);
              changeTableHeightOffset();
            }
          }
        });

        const parent = ref.current.parentElement;
        if (parent !== null) {
          const grandParent = parent.parentElement;
          if (grandParent !== null) {
            tmpObserver.observe(grandParent);
          }
        }

        setResizeObserver(tmpObserver);
      }
    }
    return () => {
      window.removeEventListener('resize', resizeCallback);
    };
  }, [resizeCallback, ref, resizeObserver, tableHeight, changeTableHeightOffset]);

  const { data: redistributionData, isFetching } = useGetCalculationCategoryPairingsQuery(params);

  useEffect(() => {
    //resetting paging after category change
    setParams(prevValue => {
      const newParams = {
        ...prevValue,
        categoryId,
        skip: 0,
        top: CALCULATION_REDISTRIBUTION_PAGE_SIZE,
      };

      const conditions = buildRedistributionConditions(prevValue.conditions, {
        brandId: redistributionFilterBrandId,
        sourceSkuClassIds: redistributionFilterSourceSkuClassIds,
        targetSkuClassIds: redistributionFilterTargetSkuClassIds,
        outOfStock: redistributionFilterOutOfStock,
        forced: redistributionFilterForced,
      });

      if (conditions.length > 0) {
        newParams.conditions = conditions;
      } else {
        delete newParams.conditions;
      }

      debugLog('setting params %s', JSON.stringify(newParams));
      return newParams;
    });
  }, [
    categoryId,
    redistributionFilterBrandId,
    redistributionFilterForced,
    redistributionFilterOutOfStock,
    redistributionFilterSourceSkuClassIds,
    redistributionFilterTargetSkuClassIds,
  ]);

  useEffect(() => {
    const dataTotalCount = redistributionData?.totalCount ?? 0;
    debugLog(`setting pairing count to ${dataTotalCount}`);
    dispatch(setRedistributionPairingCount(dataTotalCount));

    //if there is data, we need to resize the table so it fits the content
    if (dataTotalCount > 0) {
      resizeCallback();
    }
  }, [dispatch, redistributionData, resizeCallback]);

  const selectedRowRef = useRef(selectedItem);

  const selectRow = useCallback(
    (record: SkuRedistributionResponse) => {
      dispatch(selectSKURedistribution(record));

      if (selectedRowRef.current !== undefined) {
        const oldKey = getRowKey(selectedRowRef.current);
        const oldRow = document.querySelector(`tr[data-row-key="${oldKey}"]`);
        oldRow?.classList.remove('selected-row');
      }

      selectedRowRef.current = record;

      const newKey = getRowKey(record);
      const newRow = document.querySelector(`tr[data-row-key="${newKey}"]`);
      newRow?.classList.add('selected-row');

      setSearchParams({ p: `${record.sourceSku.skuId}-${record.targetSku.skuId}` });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setSearchParams is actually stable
    [dispatch, selectedRowRef],
  );

  const rowConfig = useCallback(
    (record: SkuRedistributionResponse) => {
      return {
        onClick: (event: React.MouseEvent) => {
          event.stopPropagation();
          selectRow(record);
        },
      };
    },
    [selectRow],
  );

  if (redistributionData?.data) {
    return (
      <Panel ref={ref} $grow={1} $noAutoHeight>
        <StyledSkuRedistributionTable
          id={TABLE_ID}
          columns={columns}
          height={`${tableHeight - tableHeightOffset}px`}
          setParams={setParams}
          searchValue={searchQuery}
          dataSource={redistributionData.data}
          loading={isFetching}
          rowKey={getRowKey}
          onRow={rowConfig}
          selectedRow={selectedItem}
          onSelectRow={selectRow}
        />
      </Panel>
    );
  }
};

export default SkuRedistributionTable;
